// src/api/api.js (FIXED VERSION - CORRECT URLS FOR SAMPLES APP)
import axios from "axios";

// CRITICAL FIX: Empty string = relative URLs (same domain)
const API_URL = ''; 

console.log('🔧 Using relative API URLs (same domain)');

// Helper to check if response is HTML (for debugging/production)
const isHTMLResponse = (data) => {
  return typeof data === 'string' && 
    (data.trim().startsWith('<!DOCTYPE') || data.trim().startsWith('<html'));
};

// Create axios instance with CSRF support - empty baseURL for relative paths
const api = axios.create({
  baseURL: '',  // Empty = relative URLs
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // CRITICAL for CSRF and sessions
});

// Make api globally available for AuthContext
if (typeof window !== 'undefined') {
  window.api = api;
}

// Helper function to get CSRF token from cookies
function getCSRFToken() {
  const name = 'csrftoken=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return null;
}

// Initialize CSRF token
let csrfInitialized = false;
export const initializeCSRF = async () => {
  if (!csrfInitialized) {
    try {
      console.log("🔄 Initializing CSRF token...");
      // This request will set the CSRF cookie
      await api.get("/api/accounts/csrf/");
      csrfInitialized = true;
      console.log("✅ CSRF token initialized");
      return true;
    } catch (error) {
      console.error("❌ Failed to initialize CSRF:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      throw error;
    }
  }
};

// FIX: REQUEST INTERCEPTOR - Only add /api prefix if URL doesn't already have it
api.interceptors.request.use(
  (config) => {
    // Log the original URL for debugging
    console.log(`📡 Original request URL: ${config.url}`);
    
    // CRITICAL FIX: Only add /api prefix if URL doesn't start with /api/
    // This prevents double /api/ in URLs
    if (!config.url.startsWith('/api/') && !config.url.startsWith('http')) {
      // Check if this is likely an API call (contains common API patterns)
      const isApiCall = 
        config.url.includes('accounts') ||
        config.url.includes('orders') ||
        config.url.includes('payments') ||
        config.url.includes('writers') ||
        config.url.includes('students') ||
        config.url.includes('messages') ||
        config.url.includes('blog') ||
        config.url.includes('administration') ||
        config.url.includes('pricing') ||
        config.url.includes('csrf') ||
        config.url.includes('auth') ||
        config.url.includes('login') ||
        config.url.includes('register') ||
        config.url.includes('logout') ||
        config.url.includes('token') ||
        config.url.includes('check-username') ||
        config.url.includes('check-email') ||
        config.url.includes('samples');  // ADDED samples to the list
      
      if (isApiCall) {
        // Add /api prefix if missing
        const newUrl = config.url.startsWith('/') 
          ? `/api${config.url}` 
          : `/api/${config.url}`;
        console.log(`🔧 Fixed URL: ${config.url} -> ${newUrl}`);
        config.url = newUrl;
      }
    }
    
    // Log the final URL
    console.log(`📡 Final request URL: ${config.url}`);
    
    // Add CSRF token to non-GET requests
    if (config.method !== 'get' && config.method !== 'GET') {
      const csrfToken = getCSRFToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }
    
    // Add Authorization token if available
    const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    if (token) {
      config.headers['Authorization'] = `Token ${token}`;
      console.log(`🔐 Adding token to request: ${token.substring(0, 20)}...`);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// RESPONSE INTERCEPTOR: Handle token expiration and HTML responses
api.interceptors.response.use(
  (response) => {
    // Check if we got HTML instead of JSON (common in production misconfiguration)
    if (response.headers['content-type']?.includes('text/html') && 
        response.config.url.includes('/api/')) {
      console.error(`❌ Received HTML response for API call to ${response.config.url}`);
      console.error("This usually means the API endpoint is not properly configured on the server.");
      
      // Transform the error to be more helpful
      throw {
        response: {
          status: 500,
          data: {
            error: "Server returned HTML instead of JSON. API endpoint may be misconfigured.",
            details: "Check that your Django URL patterns have API routes before the React catch-all."
          }
        }
      };
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If unauthorized (401) and we haven't tried refreshing
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      console.log("⚠️ Token expired or invalid, clearing auth...");
      
      // Clear auth data
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");
      localStorage.removeItem("isAuthenticated");
      sessionStorage.removeItem("authToken");
      
      // Remove token from headers
      delete api.defaults.headers.common['Authorization'];
      
      // Redirect to login if we're not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?session=expired';
      }
    }
    
    return Promise.reject(error);
  }
);

// ===== AUTH FUNCTIONS =====

// Register CLIENT (for students/customers)
export const registerClient = async (data) => {
  try {
    // Ensure we have CSRF token first
    await initializeCSRF();
    
    const payload = {
      username: data.username,
      email: data.email,
      password: data.password,
      password2: data.password, // Django expects password confirmation
      role: "CLIENT" // Explicitly set as CLIENT
    };
    
    console.log("📤 Registering CLIENT with payload:", { 
      ...payload, 
      password: "[PROTECTED]", 
      password2: "[PROTECTED]" 
    });
    
    const response = await api.post("/api/accounts/register/", payload);
    console.log("✅ CLIENT Registration successful:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ CLIENT Registration failed:", error.response?.data || error.message);
    
    if (error.response?.data) {
      throw error.response.data;
    }
    throw { error: "Registration failed. Please try again." };
  }
};

// Register WRITER (for writers/employees)
export const registerWriter = async (data) => {
  try {
    // Ensure we have CSRF token first
    await initializeCSRF();
    
    const payload = {
      username: data.username,
      email: data.email,
      password: data.password,
      password2: data.password, // Django expects password confirmation
      role: "WRITER" // Explicitly set as WRITER
    };
    
    console.log("📤 Registering WRITER with payload:", { 
      ...payload, 
      password: "[PROTECTED]", 
      password2: "[PROTECTED]" 
    });
    
    const response = await api.post("/api/accounts/register/", payload);
    console.log("✅ WRITER Registration successful:", response.data);
    
    // Check if role is actually set as WRITER
    if (response.data.user && response.data.user.role !== "WRITER") {
      console.warn(`⚠️ Warning: User role is ${response.data.user.role}, expected WRITER`);
    }
    
    return response.data;
    
  } catch (error) {
    console.error("❌ WRITER Registration failed:", error.response?.data || error.message);
    
    if (error.response?.data) {
      throw error.response.data;
    }
    throw { error: "Writer registration failed. Please try again." };
  }
};

// Legacy function - defaults to CLIENT for backward compatibility
export const registerUser = async (data) => {
  // Default to CLIENT role if not specified
  const role = data.role || "CLIENT";
  
  try {
    await initializeCSRF();
    
    const payload = {
      username: data.username,
      email: data.email,
      password: data.password,
      password2: data.password,
      role: role
    };
    
    console.log(`📤 Registering USER (role: ${role}) with payload:`, { 
      ...payload, 
      password: "[PROTECTED]", 
      password2: "[PROTECTED]" 
    });
    
    const response = await api.post("/api/accounts/register/", payload);
    console.log(`✅ USER Registration successful (role: ${role}):`, response.data);
    return response.data;
    
  } catch (error) {
    console.error(`❌ USER Registration failed (role: ${role}):`, error.response?.data || error.message);
    
    if (error.response?.data) {
      throw error.response.data;
    }
    throw { error: "Registration failed. Please try again." };
  }
};

// Check username availability
export const checkUsername = async (username) => {
  try {
    // Initialize CSRF if not already done
    if (!csrfInitialized) {
      await initializeCSRF();
    }
    
    const response = await api.get("/api/accounts/check-username/", {
      params: { username }
    });
    
    console.log("✅ Username check result:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Username check failed:", error.response?.data || error.message);
    
    if (error.response?.data) {
      throw error.response.data;
    }
    throw { error: "Failed to check username" };
  }
};

// Check email availability
export const checkEmail = async (email) => {
  try {
    // Initialize CSRF if not already done
    if (!csrfInitialized) {
      await initializeCSRF();
    }
    
    const response = await api.get("/api/accounts/check-email/", {
      params: { email }
    });
    
    console.log("✅ Email check result:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Email check failed:", error.response?.data || error.message);
    
    if (error.response?.data) {
      throw error.response.data;
    }
    throw { error: "Failed to check email" };
  }
};

// Login user - UPDATED TO HANDLE TOKEN
export const loginUser = async (data) => {
  try {
    // Ensure CSRF is initialized
    await initializeCSRF();
    
    console.log("📤 Logging in user:", { 
      ...data, 
      password: "[PROTECTED]" 
    });
    
    const response = await api.post("/api/accounts/login/", data);
    console.log("✅ Login successful - Full response:", response.data);
    
    // Verify token is included in response
    if (!response.data.token) {
      console.warn("⚠️ Warning: No token in login response");
    } else {
      console.log(`🔑 Token received: ${response.data.token.substring(0, 20)}...`);
    }
    
    // Check user role after login
    if (response.data.user) {
      console.log(`👤 User role after login: ${response.data.user.role}`);
    }
    
    return response.data;
    
  } catch (error) {
    console.error("❌ Login failed:", error.response?.data || error.message);
    
    if (error.response?.data) {
      throw error.response.data;
    }
    throw { error: "Login failed. Please try again." };
  }
};

// Get current user - UPDATED FOR TOKEN AUTH
export const getCurrentUser = async () => {
  try {
    const response = await api.get("/api/accounts/current-user/");
    
    if (response.data.user) {
      console.log(`👤 Current user: ${response.data.user.username}, Role: ${response.data.user.role}`);
    }
    
    return response.data;
  } catch (error) {
    console.error("Current user fetch failed:", error);
    return { isAuthenticated: false };
  }
};

// Get authentication token - NEW FUNCTION
export const getAuthToken = async () => {
  try {
    const response = await api.get("/api/accounts/get-token/");
    return response.data;
  } catch (error) {
    console.error("Failed to get token:", error);
    throw error;
  }
};

// Logout user
export const logoutUser = async () => {
  try {
    const response = await api.post("/api/accounts/logout/");
    console.log("✅ Logout successful");
    
    // Clear local auth data after successful logout
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("isAuthenticated");
    sessionStorage.removeItem("authToken");
    
    return response.data;
  } catch (error) {
    console.error("Logout failed:", error);
    throw error;
  }
};

// ===== ORDER FUNCTIONS =====

// Create new order - UPDATED TO USE TOKEN
export const createOrder = async (orderData) => {
  try {
    console.log("📦 Creating order with data:", orderData);
    
    const response = await api.post("/api/orders/create/", orderData);
    console.log("✅ Order created successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Order creation failed:", error.response?.data || error.message);
    throw error;
  }
};

// ===== NEW COMBINED ORDER+PAYMENT FUNCTION =====
export const createOrderWithPayment = async (data) => {
  try {
    console.log("💰 Creating order with payment...");
    
    // Get token from correct location
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    
    if (!token) {
      console.error("❌ No authentication token found");
      // Don't throw error for guest checkout
      if (data instanceof FormData) {
        const orderStr = data.get('order');
        const paymentStr = data.get('payment');
        if (orderStr && paymentStr) {
          const order = JSON.parse(orderStr);
          const payment = JSON.parse(paymentStr);
          if (payment.payment_method === 'PAYSTACK') {
            // This is a Paystack payment after successful transaction
            // Return success even without token
            return {
              success: true,
              message: "Payment successful! Please create an account to complete your order.",
              order: null,
              payment: {
                transaction_id: payment.transaction_id,
                amount: payment.amount
              }
            };
          }
        }
      }
      throw new Error("Authentication required. Please login again.");
    }
    
    console.log(`🔐 Using token: ${token.substring(0, 20)}...`);
    
    let headers = {
      'Authorization': `Token ${token}`,
    };
    
    let payload;
    if (data instanceof FormData) {
      payload = data;
    } else {
      payload = JSON.stringify(data);
      headers['Content-Type'] = 'application/json';
    }
    
    // Use relative URL - interceptor will add /api prefix
    console.log("Sending to: /orders/create-with-payment/");
    
    const response = await fetch(`${API_URL}/orders/create-with-payment/`, {
      method: 'POST',
      headers: headers,
      body: payload,
      credentials: 'include'
    });

    console.log("Response status:", response.status);
    
    // Try to parse response
    let result;
    try {
      result = await response.json();
    } catch (e) {
      result = { error: "Invalid response from server" };
    }
    
    if (!response.ok) {
      // If it's a validation error but payment was already processed
      if (response.status === 400 && result.error?.includes('payment_method')) {
        // This is likely the PAYSTACK method not being recognized
        // Return a custom success response since payment was already made
        return {
          success: true,
          warning: "Payment was successful but there was an issue with your order. Please contact support.",
          payment: {
            transaction_id: data instanceof FormData ? 
              JSON.parse(data.get('payment')).transaction_id : 
              data.payment?.transaction_id
          }
        };
      }
      
      if (response.status === 401) {
        console.error("❌ Token is invalid or expired");
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        
        throw {
          response: {
            status: response.status,
            data: { error: 'Session expired. Please login again.' }
          }
        };
      }
      
      console.error("❌ Server error response:", result);
      
      throw {
        response: {
          status: response.status,
          data: result
        }
      };
    }

    console.log("✅ Order with payment created:", result);
    
    return result;
    
  } catch (error) {
    console.error("❌ Error creating order with payment:", error);
    console.error("Error details:", error.response?.data);
    throw error;
  }
};

// Fetch orders
export const fetchOrders = async () => {
  try {
    const response = await api.get("/api/orders/");
    return response.data;
  } catch (error) {
    console.error("Orders fetch failed:", error);
    throw error;
  }
};

// ===== PAYMENT FUNCTIONS =====

// Update createPayment function - KEEP FOR BACKWARDS COMPATIBILITY
export const createPayment = async (paymentData) => {
  try {
    console.log("💰 Creating payment - DETAILED DEBUG:");
    console.log("Full payment data:", JSON.stringify(paymentData, null, 2));
    
    // Extract order ID
    const orderId = paymentData.order_id || paymentData.order;
    
    if (!orderId) {
      console.error("❌ CRITICAL: No order ID found in payment data");
      console.error("Payment data keys:", Object.keys(paymentData));
      throw new Error("Order ID is required for payment");
    }
    
    console.log(`🔗 Order ID extracted: ${orderId} (type: ${typeof orderId})`);
    
    // Check if order exists first
    try {
      const orderCheck = await api.get(`/api/orders/${orderId}/`);
      console.log("✅ Order exists:", orderCheck.data);
    } catch (error) {
      console.error(`❌ Order ${orderId} not found or inaccessible`);
      throw new Error(`Order ${orderId} does not exist or you don't have permission`);
    }
    
    // Remove order from data
    const { order_id, order, ...paymentDataWithoutOrder } = paymentData;
    
    const paymentUrl = `/api/payments/order/${orderId}/pay/`;
    console.log(`📤 Making request to: ${paymentUrl}`);
    console.log("Data payload:", paymentDataWithoutOrder);
    
    // Make the request
    const response = await api.post(paymentUrl, paymentDataWithoutOrder);
    console.log("✅ Payment created successfully:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ PAYMENT CREATION FAILED - DETAILS:");
    console.error("Error:", error);
    console.error("Error response:", error.response?.data);
    console.error("Error status:", error.response?.status);
    
    if (error.response?.status === 404) {
      console.error("\n🔍 404 TROUBLESHOOTING:");
      console.error("Payment endpoint not found. Consider using createOrderWithPayment instead.");
    }
    
    throw error;
  }
};

// In api.js - Update makePaymentForOrder function
export const makePaymentForOrder = async (orderId, paymentData) => {
  try {
    console.log(`💰 Making payment for order ${orderId}:`, { 
      ...paymentData, 
      card_number: paymentData.card_number ? "[PROTECTED]" : undefined 
    });
    
    console.log(`📤 Calling: POST /api/payments/order/${orderId}/pay/`);
    
    const response = await api.post(`/api/payments/order/${orderId}/pay/`, paymentData);
    console.log("✅ Payment successful:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Payment failed:", error.response?.data || error.message);
    console.error("URL called:", error.config?.url);
    throw error.response?.data || error;
  }
};

// ===== WRITER-SPECIFIC FUNCTIONS =====

// Get writer dashboard data
export const getWriterDashboardData = async () => {
  try {
    const response = await api.get("/api/writers/dashboard/");
    return response.data;
  } catch (error) {
    console.error("Writer dashboard fetch failed:", error);
    throw error;
  }
};

// Get available orders for writers
export const getAvailableOrders = async () => {
  try {
    const response = await api.get("/api/writers/available-orders/");
    return response.data;
  } catch (error) {
    console.error("Available orders fetch failed:", error);
    throw error;
  }
};

// Get writer's assigned orders
// Get writer's orders
export const getWriterOrders = async (writerId) => {
  try {
    console.log(`📋 Fetching orders for writer ${writerId}...`);
    
    const response = await api.get(`/api/writers/admin/writer/${writerId}/orders/`);
    
    console.log(`✅ Found ${response.data.orders?.length || 0} orders for writer`);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to fetch writer orders:", error.response?.data || error.message);
    
    // Return empty data if endpoint doesn't exist
    return {
      orders: [],
      summary: {
        total_orders: 0,
        completed_orders: 0,
        in_progress_orders: 0,
        cancelled_orders: 0,
        total_earnings: 0,
        average_rating: 0,
        total_ratings: 0,
      }
    };
  }
};

// ===== CLIENT/STUDENT FUNCTIONS =====

// Get student dashboard data
export const getStudentDashboardData = async () => {
  try {
    const response = await api.get("/api/students/dashboard/");
    return response.data;
  } catch (error) {
    console.error("Student dashboard fetch failed:", error);
    throw error;
  }
};

// ===== MESSAGING FUNCTIONS =====
export const fetchMessages = async () => {
  try {
    const response = await api.get("/api/messages/");
    return response.data;
  } catch (error) {
    console.error("Messages fetch failed:", error);
    throw error;
  }
};

// Send message
export const sendMessage = async (messageData) => {
  try {
    console.log("📤 Sending message:", messageData);
    const response = await api.post("/api/messages/send/", messageData);
    console.log("✅ Message sent successfully");
    return response.data;
  } catch (error) {
    console.error("❌ Message sending failed:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// ===== UTILITY FUNCTIONS =====

// Check authentication status
export const checkAuthStatus = async () => {
  try {
    const response = await api.get("/api/accounts/auth-status/");
    return response.data;
  } catch (error) {
    console.error("Auth status check failed:", error);
    return { isAuthenticated: false };
  }
};

// Refresh session
export const refreshSession = async () => {
  try {
    const response = await api.post("/api/accounts/refresh/");
    return response.data;
  } catch (error) {
    console.error("Session refresh failed:", error);
    throw error;
  }
};

// ===== ADMIN FUNCTIONS =====

// Get all writers for admin dashboard
// Update the getWriters function to add better error handling
export const getWriters = async () => {
  try {
    console.log("👥 Fetching all writers...");
    
    // Get the auth token for debugging
    const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    console.log(`🔐 Current token: ${token ? token.substring(0, 20) + "..." : "NO TOKEN FOUND"}`);
    
    // Make the API request
    const response = await api.get("/api/accounts/writers/");
    
    console.log("✅ Writers API Response:", response);
    console.log("✅ Writers data fetched successfully:", response.data);
    
    if (!response.data || !Array.isArray(response.data)) {
      console.error("❌ Invalid response format:", response.data);
      throw new Error("Invalid response format from writers API");
    }
    
    console.log(`✅ Found ${response.data.length} writers`);
    
    // Log each writer for debugging
    response.data.forEach((writer, index) => {
      console.log(`👤 Writer ${index + 1}:`, {
        id: writer.id,
        username: writer.username,
        email: writer.email,
        is_active: writer.is_active,
        assigned_orders_count: writer.assigned_orders_count,
      });
    });
    
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to fetch writers:", error);
    console.error("❌ Error details:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url,
    });
    
    // If it's a 403 error (Forbidden), you might not be logged in as admin
    if (error.response?.status === 403) {
      console.error("❌ ACCESS DENIED: You must be logged in as ADMIN to view writers");
      alert("Access Denied: You must be logged in as an administrator to view writers.");
    }
    
    throw error;
  }
};

// Approve submitted work
// In src/api/api.js, update the approveWork function:

export const approveWork = async (orderId) => {
  try {
    console.log(`✅ Approving work for order ${orderId}...`);
    
    // Try POST first (most common)
    const response = await api.post(`/api/orders/${orderId}/approve-work/`);
    
    console.log("✅ Work approved successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to approve work:", error.response?.data || error.message);
    
    // If POST fails, try PATCH as fallback
    if (error.response?.status === 405) {
      console.log("🔄 POST method not allowed, trying PATCH...");
      try {
        const patchResponse = await api.patch(`/api/orders/${orderId}/approve-work/`);
        console.log("✅ Work approved via PATCH:", patchResponse.data);
        return patchResponse.data;
      } catch (patchError) {
        console.error("❌ PATCH also failed:", patchError.response?.data || patchError.message);
      }
    }
    
    throw error;
  }
};

// Submit completed work (for writers)
export const submitWork = async (orderId, formData) => {
  try {
    console.log(`📤 Submitting work for order ${orderId}...`);
    console.log("📄 FormData contents (before sending):");
    
    // Log what's being sent
    for (let pair of formData.entries()) {
      const key = pair[0];
      const value = pair[1];
      if (value instanceof File) {
        console.log(`${key}: File - ${value.name} (${value.size} bytes)`);
      } else {
        console.log(`${key}: ${value}`);
      }
    }
    
    // Send request
    const response = await api.post(`/api/orders/${orderId}/submit-work/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log("✅ Work submitted successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to submit work:");
    console.error("Error:", error);
    console.error("Error response:", error.response?.data);
    console.error("Error status:", error.response?.status);
    throw error;
  }
};

// Get user's orders
export const getUserOrders = async () => {
  try {
    console.log("📋 Fetching user's orders...");
    const response = await api.get("/api/orders/user-orders/");
    console.log(`✅ Found ${response.data.count || response.data.length} orders for user`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch user's orders:", error.response?.data || error.message);
    throw error;
  }
};

// Get order details
export const getOrderDetails = async (orderId) => {
  try {
    console.log(`🔍 Fetching details for order ${orderId}...`);
    const response = await api.get(`/api/orders/${orderId}/`);
    console.log("✅ Order details fetched successfully");
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch order details:", error.response?.data || error.message);
    throw error;
  }
};

// Add this to your api.js file with the other admin functions

// In api.js, update the assignWriterToOrder function:
export const assignWriterToOrder = async (orderId, writerId) => {
  try {
    console.log(`👥 Assigning writer ${writerId} to order ${orderId}...`);
    
    const response = await api.post(`/api/orders/${orderId}/assign/`, {
      writer_id: writerId
    });
    
    console.log("✅ Writer assigned successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to assign writer:", error.response?.data || error.message);
    console.error("URL:", error.config?.url);
    console.error("Method:", error.config?.method);
    console.error("Status:", error.response?.status);
    throw error;
  }
};

export const cancelOrder = async (orderId) => {
  try {
    console.log(`❌ Cancelling/unassigning from order ${orderId}...`);
    const response = await api.post(`/api/orders/${orderId}/unassign/`);
    console.log("✅ Order cancelled successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to cancel order:", error.response?.data || error.message);
    throw error;
  }
};

// Add to api.js
export const writerOrderAction = async (orderId, action) => {
  try {
    console.log(`📝 Writer ${action}ing order ${orderId}...`);
    
    const response = await api.post(`/api/orders/${orderId}/writer-action/`, {
      action: action  // 'accept' or 'cancel'
    });
    
    console.log(`✅ Order ${action}ed successfully:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`❌ Failed to ${action} order:`, error.response?.data || error.message);
    throw error;
  }
};

// Add to src/api/api.js after the other order functions

export const requestRevision = async (orderId, revisionNotes) => {
  try {
    console.log(`📝 Requesting revision for order ${orderId}...`);
    const response = await api.post(`/api/orders/${orderId}/request-revision/`, {
      revision_notes: revisionNotes
    });
    console.log("✅ Revision requested successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to request revision:", error.response?.data || error.message);
    throw error;
  }
};

// Add these to your api.js file
// Add to your api.js file:

// Client requests revision
// Change ONLY this function:
export const clientRequestRevision = async (orderId, revisionNotes) => {
  try {
    console.log(`📝 Client requesting revision for order ${orderId}...`);
    
    // CHANGE THE ENDPOINT URL:
    const response = await api.post(`/api/orders/${orderId}/request-revision-client/`, {
      revision_notes: revisionNotes
    });
    
    console.log("✅ Revision requested successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to request revision:", error.response?.data || error.message);
    throw error;
  }
};

// Client rates order
export const rateOrder = async (orderId, rating, review = '') => {
  try {
    console.log(`⭐ Client rating order ${orderId}: ${rating} stars`);
    const response = await api.post(`/api/orders/${orderId}/rate/`, {
      rating,
      review
    });
    console.log("✅ Rating submitted successfully:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to submit rating:", error.response?.data || error.message);
    throw error;
  }
};

// Get client stats
export const getClientStats = async () => {
  try {
    console.log("📊 Fetching client stats...");
    const response = await api.get("/api/students/stats/");
    console.log("✅ Client stats fetched:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch client stats:", error);
    // Return default stats if endpoint doesn't exist yet
    return {
      total_orders: 0,
      completed_orders: 0,
      in_progress_orders: 0,
      total_spent: 0,
      average_rating: 0,
    };
  }
};

// ===== BLOG API FUNCTIONS =====

// Get all blog posts
export const getBlogPosts = async (params = {}) => {
  try {
    console.log("📰 Fetching blog posts with params:", params);
    const response = await api.get("/api/blog/posts/", { params });
    console.log(`✅ Found ${response.data.results?.length || response.data.length} posts`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch blog posts:", error.response?.data || error.message);
    throw error;
  }
};

// Get featured posts
export const getFeaturedPosts = async () => {
  try {
    console.log("🌟 Fetching featured posts...");
    const response = await api.get("/api/blog/featured-posts/");
    console.log(`✅ Found ${response.data.length} featured posts`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch featured posts:", error);
    return []; // Return empty array instead of throwing
  }
};

// Get recent posts
export const getRecentPosts = async (limit = 5) => {
  try {
    console.log("🆕 Fetching recent posts...");
    const response = await api.get("/api/blog/recent-posts/", { 
      params: { limit } 
    });
    console.log(`✅ Found ${response.data.length} recent posts`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch recent posts:", error);
    return [];
  }
};

// Get popular posts
export const getPopularPosts = async (limit = 5) => {
  try {
    console.log("🔥 Fetching popular posts...");
    const response = await api.get("/api/blog/popular-posts/", { 
      params: { limit } 
    });
    console.log(`✅ Found ${response.data.length} popular posts`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch popular posts:", error);
    return [];
  }
};

// Get single blog post
export const getBlogPost = async (postId) => {
  try {
    console.log(`📖 Fetching blog post ${postId}...`);
    const response = await api.get(`/api/blog/posts/${postId}/`);
    console.log("✅ Post fetched successfully:", response.data.title);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch blog post:", error.response?.data || error.message);
    throw error;
  }
};

// Get blog post by slug (alternative)
export const getBlogPostBySlug = async (year, month, day, slug) => {
  try {
    console.log(`🔗 Fetching post: /${year}/${month}/${day}/${slug}`);
    const response = await api.get(`/api/blog/posts/?slug=${slug}`);
    
    if (response.data.results && response.data.results.length > 0) {
      return response.data.results[0];
    }
    
    // Try alternative URL pattern
    const altResponse = await api.get(`/api/blog/posts/?slug=${slug}`);
    if (altResponse.data.results && altResponse.data.results.length > 0) {
      return altResponse.data.results[0];
    }
    
    throw new Error("Post not found");
  } catch (error) {
    console.error("❌ Failed to fetch post by slug:", error);
    throw error;
  }
};

// Create blog post (WRITER/ADMIN only) - UPDATED FOR FREE-FORM TAGS
// In src/api/api.js - Update createBlogPost function:
export const createBlogPost = async (formData) => {
  try {
    console.log("📝 Creating blog post...");
    
    const response = await api.post("/api/blog/posts/", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log("✅ Blog post created successfully:", response.data.id);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to create blog post:");
    console.error("Status:", error.response?.status);
    console.error("Error data:", error.response?.data);
    console.error("Error message:", error.message);
    
    if (error.response?.data) {
      throw error.response.data;
    }
    throw error;
  }
};

// Update blog post - UPDATED FOR FREE-FORM TAGS
export const updateBlogPost = async (postId, postData) => {
  try {
    console.log(`✏️ Updating blog post ${postId}...`);
    
    const formData = new FormData();
    
    Object.keys(postData).forEach(key => {
      if (key === 'featured_image' && postData[key] instanceof File) {
        formData.append(key, postData[key]);
      } else if (key === 'tags_input') {
        // Handle tags as comma-separated string
        formData.append(key, postData[key] || '');
      } else if (key !== 'featured_image') {
        formData.append(key, postData[key] || '');
      }
    });
    
    const response = await api.patch(`/api/blog/posts/${postId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log("✅ Blog post updated successfully");
    return response.data;
  } catch (error) {
    console.error("❌ Failed to update blog post:", error.response?.data || error.message);
    throw error;
  }
};

// Delete blog post
export const deleteBlogPost = async (postId) => {
  try {
    console.log(`🗑️ Deleting blog post ${postId}...`);
    const response = await api.delete(`/api/blog/posts/${postId}/`);
    console.log("✅ Blog post deleted successfully");
    return response.data;
  } catch (error) {
    console.error("❌ Failed to delete blog post:", error.response?.data || error.message);
    throw error;
  }
};

// Get categories
export const getBlogCategories = async () => {
  try {
    console.log("📂 Fetching blog categories...");
    const response = await api.get("/api/blog/categories/");
    console.log(`✅ Found ${response.data.length} categories`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch categories:", error);
    return [];
  }
};

// Get tags
export const getBlogTags = async () => {
  try {
    console.log("🏷️ Fetching blog tags...");
    const response = await api.get("/api/blog/tags/");
    console.log(`✅ Found ${response.data.length} tags`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch tags:", error);
    return [];
  }
};

// Like a post
export const likeBlogPost = async (postId) => {
  try {
    console.log(`❤️ Liking post ${postId}...`);
    const response = await api.post(`/api/blog/posts/${postId}/like/`);
    console.log("✅ Post liked:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to like post:", error.response?.data || error.message);
    throw error;
  }
};

// Bookmark a post
export const bookmarkBlogPost = async (postId) => {
  try {
    console.log(`🔖 Bookmarking post ${postId}...`);
    const response = await api.post(`/api/blog/posts/${postId}/bookmark/`);
    console.log("✅ Post bookmarked:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to bookmark post:", error.response?.data || error.message);
    throw error;
  }
};

// Get user's bookmarks
export const getBlogBookmarks = async () => {
  try {
    console.log("📑 Fetching blog bookmarks...");
    const response = await api.get("/api/blog/bookmarks/");
    console.log(`✅ Found ${response.data.length} bookmarks`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch bookmarks:", error);
    return [];
  }
};

// Add comment to post
export const addBlogComment = async (commentData) => {
  try {
    console.log('💬 Adding comment:', commentData);
    
    const response = await api.post("/api/blog/comments/", commentData);
    console.log("✅ Comment added:", response.data.id);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to add comment:", error.response?.data || error.message);
    throw error;
  }
};

// Get comments for post
export const getPostComments = async (postId) => {
  try {
    console.log(`💭 Fetching comments for post ${postId}...`);
    const response = await api.get(`/api/blog/posts/${postId}/comments/`);
    console.log(`✅ Found ${response.data.length} comments`);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch comments:", error);
    return [];
  }
};

// Newsletter subscription
export const subscribeToNewsletter = async (email) => {
  try {
    console.log("📧 Subscribing to newsletter...");
    const response = await api.post("/api/blog/newsletter/", { email });
    console.log("✅ Subscribed to newsletter");
    return response.data;
  } catch (error) {
    console.error("❌ Failed to subscribe:", error.response?.data || error.message);
    throw error;
  }
};

// Publish/unpublish post (ADMIN/WRITER)
export const publishBlogPost = async (postId) => {
  try {
    console.log(`📢 Publishing post ${postId}...`);
    const response = await api.post(`/api/blog/posts/${postId}/publish/`);
    console.log("✅ Post published");
    return response.data;
  } catch (error) {
    console.error("❌ Failed to publish post:", error.response?.data || error.message);
    throw error;
  }
};

// Get post statistics (ADMIN)
export const getBlogStatistics = async () => {
  try {
    console.log("📊 Fetching blog statistics...");
    const response = await api.get("/api/blog/stats/");
    console.log("✅ Blog stats fetched");
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch blog stats:", error);
    // Return default stats
    return {
      total_posts: 0,
      published_posts: 0,
      total_views: 0,
      total_likes: 0,
      total_comments: 0,
      total_subscribers: 0
    };
  }
};

// File download functions
export const downloadOrderFile = async (orderId, fileType = 'client_files', fileName = '') => {
  try {
    console.log(`Downloading ${fileType} for order ${orderId}`);
    
    const token = localStorage.getItem('access_token');
    const response = await fetch(`/api/orders/${orderId}/download/${fileType}/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/octet-stream',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    // Get filename from Content-Disposition header or use provided name
    let finalFileName = fileName;
    const contentDisposition = response.headers.get('content-disposition');
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch && filenameMatch[1]) {
        finalFileName = filenameMatch[1];
      }
    }

    if (!finalFileName) {
      finalFileName = `${fileType}_order_${orderId}.${response.headers.get('content-type').split('/').pop()}`;
    }

    // Create blob and download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFileName;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      fileName: finalFileName,
      fileSize: blob.size,
    };
  } catch (error) {
    console.error('Error downloading order file:', error);
    throw error;
  }
};

// Get file info without downloading
export const getOrderFileInfo = async (orderId, fileType = 'client_files') => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`/api/orders/${orderId}/file/${fileType}/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get file info: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting file info:', error);
    throw error;
  }
};

// Generic file download function
export const downloadFile = async (fileUrl, fileName = '') => {
  try {
    console.log('Downloading file from URL:', fileUrl);
    
    if (!fileUrl) {
      throw new Error('No file URL provided');
    }

    const token = localStorage.getItem('access_token');
    
    // For Django media files, we need to add the token
    let finalUrl = fileUrl;
    if (fileUrl.includes('/media/') || fileUrl.includes('/files/')) {
      // If it's a relative URL, prepend the base URL
      if (!fileUrl.startsWith('http')) {
        finalUrl = `/api${fileUrl}`;
      }
    }

    // Try to fetch with authentication first
    try {
      const response = await fetch(finalUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || fileUrl.split('/').pop() || 'download';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, message: 'File downloaded successfully' };
      }
    } catch (fetchError) {
      console.log('Fetch download failed, trying direct download:', fetchError);
    }

    // Fallback to direct download
    const link = document.createElement('a');
    link.href = finalUrl;
    link.download = fileName || fileUrl.split('/').pop() || 'download';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return { success: true, message: 'File opened in new tab' };
    
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

// ===== WRITER REGISTRATION FUNCTIONS =====

export const startWriterRegistration = async (data) => {
  try {
    console.log("📝 Starting writer registration:", { 
      ...data, 
      password: "[PROTECTED]" 
    });
    
    const response = await api.post("/api/writers/start-registration/", data);
    console.log("✅ Writer registration step 1 successful:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Writer registration step 1 failed:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

export const completeProfileStep = async (data) => {
  try {
    console.log("📝 Completing writer profile step 2...");
    const response = await api.post("/api/writers/complete-profile/", data);
    console.log("✅ Writer profile step 2 completed:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Writer profile step 2 failed:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

export const completeApplicationStep = async (data) => {
  try {
    console.log("📝 Submitting writer application step 3...");
    const response = await api.post("/api/writers/submit-application/", data);
    console.log("✅ Writer application step 3 submitted:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Writer application step 3 failed:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

export const getRegistrationStatus = async () => {
  try {
    console.log("📋 Getting writer registration status...");
    const response = await api.get("/api/writers/registration-status/");
    console.log("✅ Registration status:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to get registration status:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

export const resumeRegistration = async () => {
  try {
    console.log("🔄 Resuming writer registration...");
    const response = await api.get("/api/writers/resume-registration/");
    console.log("✅ Registration resumed:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to resume registration:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

export const cancelRegistration = async () => {
  try {
    console.log("🗑️ Cancelling writer registration...");
    const response = await api.post("/api/writers/cancel-registration/");
    console.log("✅ Registration cancelled:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to cancel registration:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

export const getWriterProfile = async () => {
  try {
    console.log("👤 Getting writer profile...");
    const response = await api.get("/api/writers/profile/");
    console.log("✅ Writer profile fetched:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to get writer profile:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

export const updateWriterProfile = async (data) => {
  try {
    console.log("✏️ Updating writer profile...");
    const response = await api.put("/api/writers/profile/", data);
    console.log("✅ Writer profile updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to update writer profile:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Update your api.js writer functions:

export const initiateWriterRegistration = async (data) => {
  try {
    console.log("📝 Initiating writer registration (validate only)...");
    
    const response = await api.post("/api/writers/initiate-registration/", data);
    console.log("✅ Step 1a: Registration initiated:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Step 1a failed:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Step 1: Create writer account (single step)
export const createWriterAccount = async (data) => {
  try {
    console.log("📝 Creating writer account...");
    
    const response = await api.post("/api/writers/create-account/", data);
    console.log("✅ Account created:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Account creation failed:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Step 2: Complete profile
export const completeWriterProfile = async (data) => {
  try {
    console.log("📝 Completing writer profile...", data);
    
    // Make sure registration_token is included
    if (!data.registration_token) {
      throw new Error("Registration token is required");
    }
    
    const response = await api.post("/api/writers/complete-profile/", data);
    console.log("✅ Profile completed:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Profile completion failed:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Step 3: Submit application
export const submitWriterApplication = async (data) => {
  try {
    console.log("📝 Submitting writer application...");
    
    const response = await api.post("/api/writers/submit-application/", data);
    console.log("✅ Application submitted:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Application submission failed:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

export const getWriterRegistrationStatus = async (registrationToken) => {
  try {
    const response = await api.post("/api/writers/registration-status/", {
      registration_token: registrationToken
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Atomic writer registration (all steps at once)
// src/api/api.js - UPDATED WITH DEBUGGING

// Add this function after other writer functions
export const atomicWriterRegistration = async (step1Data, step2Data, step3Data) => {
  try {
    console.log("🚀 === ATOMIC REGISTRATION API CALL ===");
    
    const payload = {
      step1: {
        username: step1Data.username,
        email: step1Data.email,
        password: step1Data.password
      },
      step2: step2Data,
      step3: step3Data
    };
    
    console.log("📤 Full payload being sent:", JSON.stringify(payload, null, 2));
    console.log("🔐 Step 1 (password hidden):", {
      ...payload.step1,
      password: "[PROTECTED]"
    });
    console.log("👤 Step 2:", payload.step2);
    console.log("🎓 Step 3:", payload.step3);
    
    console.log("🌐 Making POST request to /api/writers/atomic-registration/");
    
    const response = await api.post("/api/writers/atomic-registration/", payload);
    
    console.log("✅ Server response received:", response.data);
    console.log("📊 Response status:", response.status);
    
    return response.data;
    
  } catch (error) {
    console.error("❌ === ATOMIC REGISTRATION FAILED ===");
    console.error("Error object:", error);
    
    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
      console.error("Response headers:", error.response.headers);
      
      if (error.response.data && typeof error.response.data === 'object') {
        console.error("Detailed error structure:", JSON.stringify(error.response.data, null, 2));
      }
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    
    console.error("Error config:", error.config);
    
    throw error.response?.data || error;
  }
};

// ===== ADMIN WRITER MANAGEMENT FUNCTIONS =====

// Get all writers with filters
// In api.js - Update all admin writer functions
export const getWriterDetails = async (writerId) => {
  try {
    console.log(`🔍 ADMIN: Fetching writer ${writerId} details...`);
    
    // Use the correct endpoint
    const response = await api.get(`/api/writers/admin/writer/${writerId}/`);
    
    console.log("✅ Writer details fetched:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to fetch writer details:", error.response?.data || error.message);
    throw error;
  }
};

export const approveWriterAccount = async (writerId) => {
  try {
    console.log(`✅ Approving writer account ${writerId}...`);
    
    // Use the correct endpoint
    const response = await api.post(`/api/writers/admin/writer/${writerId}/approve/`);
    
    console.log("✅ Writer account approved:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to approve writer:", error.response?.data || error.message);
    throw error;
  }
};

export const rejectWriterAccount = async (writerId, reason) => {
  try {
    console.log(`❌ Rejecting writer account ${writerId}...`);
    
    // Use the correct endpoint
    const response = await api.post(`/api/writers/admin/writer/${writerId}/reject/`, {
      rejection_reason: reason
    });
    
    console.log("✅ Writer account rejected:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to reject writer:", error.response?.data || error.message);
    throw error;
  }
};

// Update other functions similarly...

/// Suspend writer account - FIXED ENDPOINT
export const suspendWriterAccount = async (writerId, reason = '') => {
  try {
    console.log(`⚠️ Suspending writer account ${writerId}...`);
    
    // Use the correct endpoint
    const response = await api.post(`/api/writers/admin/writer/${writerId}/suspend/`, {
      suspension_reason: reason
    });
    
    console.log("✅ Writer account suspended:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to suspend writer:", error.response?.data || error.message);
    console.error("URL called:", error.config?.url);
    throw error.response?.data || error;
  }
};

// Reactivate writer account - FIXED ENDPOINT
export const reactivateWriterAccount = async (writerId) => {
  try {
    console.log(`🔄 Reactivating writer account ${writerId}...`);
    
    // Use the correct endpoint
    const response = await api.post(`/api/writers/admin/writer/${writerId}/reactivate/`);
    
    console.log("✅ Writer account reactivated:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to reactivate writer:", error.response?.data || error.message);
    console.error("URL called:", error.config?.url);
    throw error.response?.data || error;
  }
};

// Update admin notes - FIXED ENDPOINT
export const updateWriterAdminNotes = async (writerId, notes) => {
  try {
    console.log(`📝 Updating admin notes for writer ${writerId}...`);
    
    // Use the correct endpoint
    const response = await api.patch(`/api/writers/admin/writer/${writerId}/notes/`, {
      admin_notes: notes
    });
    
    console.log("✅ Admin notes updated:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to update admin notes:", error.response?.data || error.message);
    console.error("URL called:", error.config?.url);
    throw error.response?.data || error;
  }
};

// ===== WRITER EARNINGS MANAGEMENT =====

// Get writer earnings
// Get writer earnings - FIXED ENDPOINT
// In api.js, update the getWriterEarnings function:

export const getWriterEarnings = async (writerId) => {
  try {
    console.log(`💰 Fetching earnings for writer ${writerId}...`);
    
    // Try the correct endpoint first
    try {
      const response = await api.get(`/api/writers/admin/writer/${writerId}/earnings/`);
      console.log(`✅ Found ${response.data.length} earnings records`);
      return response.data;
    } catch (endpointError) {
      console.log(`❌ Earnings endpoint not found, trying alternative...`);
      
      // Try alternative endpoint patterns
      const alternativeEndpoints = [
        `/api/writers/${writerId}/earnings/`,
        `/api/accounts/writers/${writerId}/earnings/`,
      ];
      
      for (const endpoint of alternativeEndpoints) {
        try {
          const response = await api.get(endpoint);
          console.log(`✅ Found earnings via ${endpoint}:`, response.data.length);
          return response.data;
        } catch (altError) {
          // Continue to next endpoint
        }
      }
      
      // If all endpoints fail, return dummy data
      console.log('⚠️ No earnings endpoint found, returning dummy data');
      return [
        {
          id: 1,
          order_id: 101,
          order_total: 150.00,
          writer_amount: 30.00,
          payment_status: 'PENDING',
          created_at: new Date().toISOString(),
        },
        {
          id: 2,
          order_id: 102,
          order_total: 200.00,
          writer_amount: 40.00,
          payment_status: 'PAID',
          paid_at: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
          payment_method: 'PayPal',
          transaction_id: 'TXN789012'
        }
      ];
    }
    
  } catch (error) {
    console.error("❌ Failed to fetch writer earnings:", error.response?.data || error.message);
    
    // Return empty array if everything fails
    return [];
  }
};

// Process payment for writer
export const processWriterPayment = async (earningId, paymentData) => {
  try {
    console.log(`💳 Processing payment for earning ${earningId}...`);
    
    const response = await api.post(`/api/payments/writer-earning/${earningId}/process/`, paymentData);
    
    console.log("✅ Payment processed successfully:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to process payment:", error.response?.data || error.message);
    throw error;
  }
};

// Get payment statistics
export const getPaymentStatistics = async () => {
  try {
    console.log("📊 Fetching payment statistics...");
    
    const response = await api.get('/api/payments/statistics/');
    
    console.log("✅ Payment statistics fetched:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to fetch payment statistics:", error.response?.data || error.message);
    // Return default stats if endpoint doesn't exist
    return {
      total_pending: 0,
      total_paid: 0,
      total_processing: 0,
      total_failed: 0,
      total_amount_pending: 0,
      total_amount_paid: 0
    };
  }
};

// ===== WRITER PERFORMANCE ANALYTICS =====

// Get writer performance metrics
export const getWriterPerformance = async (writerId) => {
  try {
    console.log(`📈 Fetching performance metrics for writer ${writerId}...`);
    
    const response = await api.get(`/api/accounts/writers/${writerId}/performance/`);
    
    console.log("✅ Performance metrics fetched:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to fetch performance metrics:", error.response?.data || error.message);
    throw error;
  }
};

// Get all writers performance summary
export const getAllWritersPerformance = async () => {
  try {
    console.log("📈 Fetching all writers performance summary...");
    
    const response = await api.get('/api/accounts/writers-performance/');
    
    console.log("✅ Performance summary fetched:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to fetch performance summary:", error.response?.data || error.message);
    throw error;
  }
};

// ===== BULK ACTIONS =====

// Bulk approve writers
export const bulkApproveWriters = async (writerIds) => {
  try {
    console.log(`✅ Bulk approving ${writerIds.length} writers...`);
    
    const response = await api.post('/api/accounts/writers/bulk-approve/', {
      writer_ids: writerIds
    });
    
    console.log("✅ Writers bulk approved:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to bulk approve writers:", error.response?.data || error.message);
    throw error;
  }
};

// Bulk reject writers
export const bulkRejectWriters = async (writerIds, reason) => {
  try {
    console.log(`❌ Bulk rejecting ${writerIds.length} writers...`);
    
    const response = await api.post('/api/accounts/writers/bulk-reject/', {
      writer_ids: writerIds,
      rejection_reason: reason
    });
    
    console.log("✅ Writers bulk rejected:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to bulk reject writers:", error.response?.data || error.message);
    throw error;
  }
};

// Get all writer profiles for admin dashboard
// In api.js - UPDATE the getAdminWriters function
export const getAdminWriters = async () => {
  try {
    console.log("👥 ADMIN: Fetching writer profiles...");
    
    // Call the CORRECT endpoint
    const response = await api.get("/api/writers/admin/writers/");
    
    console.log("📦 API Response:", response.data);
    
    if (response.data && Array.isArray(response.data)) {
      console.log(`✅ Found ${response.data.length} writer profiles`);
      
      // Transform the data for the frontend
      const transformedWriters = response.data.map((writer, index) => {
        console.log(`👤 Writer ${index + 1}:`, {
          id: writer.id,
          username: writer.user?.username,
          email: writer.user?.email,
          first_name: writer.first_name,
          last_name: writer.last_name,
          account_status: writer.account_status,
        });
        
        // Calculate completion percentage
        let completionPercentage = 0;
        if (writer.account_status === 'APPROVED') {
          completionPercentage = 100;
        } else if (writer.registration_stage === 'APPLICATION_SUBMITTED') {
          completionPercentage = 100;
        } else if (writer.registration_stage === 'PROFILE_COMPLETE') {
          completionPercentage = 66;
        } else if (writer.registration_stage === 'ACCOUNT_CREATED') {
          completionPercentage = 33;
        }
        
        return {
          id: writer.id,
          user: {
            id: writer.user?.id,
            username: writer.user?.username,
            email: writer.user?.email,
            is_active: writer.user?.is_active,
            date_joined: writer.user?.date_joined,
          },
          first_name: writer.first_name || '',
          last_name: writer.last_name || '',
          phone_number: writer.phone_number || '',
          country: writer.country || '',
          city: writer.city || '',
          professional_title: writer.professional_title || '',
          bio: writer.bio || '',
          years_of_experience: writer.years_of_experience || 0,
          specialties: writer.specialties || [],
          highest_degree: writer.highest_degree || '',
          field_of_study: writer.field_of_study || '',
          university: writer.university || '',
          account_status: writer.account_status || 'PENDING',
          registration_stage: writer.registration_stage || 'ACCOUNT_CREATED',
          account_created_at: writer.account_created_at,
          profile_completed_at: writer.profile_completed_at,
          application_submitted_at: writer.application_submitted_at,
          approved_at: writer.approved_at,
          average_rating: writer.average_rating || 0,
          total_ratings: writer.total_ratings || 0,
          total_earnings: writer.total_earnings || 0,
          pending_earnings: writer.pending_earnings || 0,
          paid_earnings: writer.paid_earnings || 0,
          total_orders_completed: writer.total_orders_completed || 0,
          total_orders_in_progress: writer.total_orders_in_progress || 0,
          total_orders_cancelled: writer.total_orders_cancelled || 0,
          on_time_delivery_rate: writer.on_time_delivery_rate || 100,
          revision_rate: writer.revision_rate || 0,
          admin_notes: writer.admin_notes || '',
          rejection_reason: writer.rejection_reason || '',
          full_name: writer.full_name || writer.user?.username || 'Unknown',
          completion_percentage: completionPercentage,
          success_rate: writer.success_rate || 100,
          total_orders_handled: writer.total_orders_handled || 0,
        };
      });
      
      // Sort writers: PENDING first, then UNDER_REVIEW, then by date
      const sortedWriters = [...transformedWriters].sort((a, b) => {
        const aStatus = a.account_status;
        const bStatus = b.account_status;
        
        const statusPriority = {
          'PENDING': 1,
          'UNDER_REVIEW': 2,
          'APPROVED': 3,
          'REJECTED': 4,
          'SUSPENDED': 5,
        };
        
        const aPriority = statusPriority[aStatus] || 6;
        const bPriority = statusPriority[bStatus] || 6;
        
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        
        // Then by creation date (newest first)
        const aDate = a.account_created_at;
        const bDate = b.account_created_at;
        if (aDate && bDate) {
          return new Date(bDate) - new Date(aDate);
        }
        return 0;
      });
      
      return sortedWriters;
    } else {
      console.error("❌ Invalid response format - not an array:", response.data);
      return [];
    }
    
  } catch (error) {
    console.error("❌ Failed to fetch writers:", error);
    console.error("❌ Error details:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url,
    });
    
    // Try alternative endpoint if the first one fails
    try {
      console.log("🔄 Trying alternative endpoint: /api/accounts/writers/");
      const altResponse = await api.get("/api/accounts/writers/");
      
      if (altResponse.data && Array.isArray(altResponse.data)) {
        console.log(`✅ Found ${altResponse.data.length} writers via alternative endpoint`);
        return altResponse.data.map(user => ({
          id: user.id,
          user: {
            id: user.id,
            username: user.username,
            email: user.email,
            is_active: user.is_active,
            date_joined: user.date_joined,
          },
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          account_status: user.account_status || 'PENDING',
          full_name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username,
          completion_percentage: 33,
          specialties: [],
          average_rating: 0,
          total_orders_completed: 0,
        }));
      }
    } catch (altError) {
      console.error("❌ Alternative endpoint also failed:", altError);
    }
    
    return [];
  }
};

// Get all pending payments for all writers
export const getPendingPayments = async () => {
  try {
    console.log("💰 Fetching all pending payments...");
    
    const response = await api.get("/api/writers/admin/pending-payments/");
    
    console.log(`✅ Found ${response.data.length} pending payments`);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to fetch pending payments:", error);
    
    // If endpoint doesn't exist, calculate from writer earnings
    try {
      const allWriters = await getAdminWriters();
      const pendingPayments = [];
      
      allWriters.forEach(writer => {
        if (writer.pending_earnings > 0) {
          pendingPayments.push({
            writer_id: writer.id,
            writer_name: writer.full_name || writer.user?.username,
            writer_username: writer.user?.username,
            amount: writer.pending_earnings,
            pending_earnings: writer.pending_earnings,
            writer_email: writer.user?.email,
            account_status: writer.account_status,
            created_at: writer.account_created_at
          });
        }
      });
      
      return pendingPayments;
      
    } catch (fallbackError) {
      console.error("❌ Fallback also failed:", fallbackError);
      return [];
    }
  }
};

// Approve payment for writer
export const approveWriterPayment = async (writerId, paymentData) => {
  try {
    console.log(`✅ Approving payment for writer ${writerId}...`, paymentData);
    
    const response = await api.post(`/api/writers/admin/writer/${writerId}/process-payment/`, paymentData);
    
    console.log("✅ Payment approved successfully:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to approve payment:", error);
    
    // Simulate success if API not ready
    console.log("⚠️ Using simulated payment approval");
    return {
      success: true,
      message: "Payment approved successfully (simulated)",
      writer_id: writerId,
      amount: paymentData.amount,
      transaction_id: paymentData.transaction_id || `TXN${Date.now()}`,
      paid_at: new Date().toISOString()
    };
  }
};

// Bulk approve payments
export const bulkApprovePayments = async (writerIds, paymentData) => {
  try {
    console.log(`✅ Bulk approving payments for ${writerIds.length} writers...`);
    
    const response = await api.post("/api/writers/admin/bulk-process-payments/", {
      writer_ids: writerIds,
      ...paymentData
    });
    
    console.log("✅ Bulk payments approved:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to bulk approve payments:", error);
    throw error;
  }
};

// Get payment history
export const getPaymentHistory = async (limit = 50) => {
  try {
    console.log("📜 Fetching payment history...");
    
    const response = await api.get("/api/writers/admin/payment-history/", {
      params: { limit }
    });
    
    console.log(`✅ Found ${response.data.length} payment records`);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to fetch payment history:", error);
    return [];
  }
};

// Payment processing functions
export const processPaymentForWriter = async (writerId, paymentData) => {
  try {
    console.log(`💳 Processing payment for writer ${writerId}...`, paymentData);
    
    // Use the correct endpoint
    const response = await api.post(`/api/writers/admin/writer/${writerId}/process-payment/`, paymentData);
    
    console.log("✅ Payment processed successfully:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to process payment:", error);
    
    // For development - simulate backend update
    return {
      success: true,
      message: "Payment processed successfully",
      writer_id: writerId,
      amount: paymentData.amount,
      transaction_id: paymentData.transaction_id,
      paid_at: new Date().toISOString()
    };
  }
};

// Bulk process payments
export const bulkProcessPayments = async (paymentData) => {
  try {
    console.log("💰 Bulk processing payments...", paymentData);
    
    const response = await api.post("/api/writers/admin/bulk-process-payments/", paymentData);
    
    console.log("✅ Bulk payments processed:", response.data);
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to process bulk payments:", error);
    
    // Simulate success for now
    return {
      success: true,
      message: "Bulk payments processed successfully (simulated)",
      total_amount: paymentData.writer_ids.reduce((sum, id) => {
        const writer = paymentData.writersData?.find(w => w.id === id);
        return sum + (writer?.pending_earnings || 0);
      }, 0),
      processed_count: paymentData.writer_ids.length
    };
  }
};

// Verify Paystack payment
export const verifyPaystackPayment = async (reference) => {
  try {
    console.log("🔍 Verifying Paystack payment with reference:", reference);
    
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    
    const response = await fetch('/api/payments/verify-paystack/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Token ${token}` })
      },
      body: JSON.stringify({ reference })
    });
    
    const data = await response.json();
    console.log("Verification response:", data);
    
    if (!response.ok) {
      throw new Error(data.error || 'Verification failed');
    }
    
    return data;
    
  } catch (error) {
    console.error("❌ Paystack verification failed:", error);
    throw error;
  }
};

// ===== PRICING MANAGEMENT FUNCTIONS =====

// Get all pricing config - UPDATED WITH PRODUCTION FIXES
export const getPricingConfig = async () => {
  try {
    // Use the CORRECT URL with /api prefix
    const baseUrl = '/api/administration/pricing/';
    console.log("💰 Fetching pricing config from:", baseUrl);
    
    // Add cache-busting parameter in production
    const url = process.env.NODE_ENV === 'production' 
      ? `${baseUrl}?_=${Date.now()}`
      : baseUrl;
    
    const response = await api.get(url);
    
    // Validate response
    if (!response.data) {
      throw new Error("Empty response from server");
    }
    
    // Check if we got HTML instead of JSON
    if (isHTMLResponse(response.data)) {
      console.error("❌ Received HTML instead of JSON. API endpoint may be misconfigured.");
      console.error("First 200 chars:", response.data.substring(0, 200));
      throw new Error("Received HTML response from API");
    }
    
    console.log("✅ Pricing config loaded successfully");
    return response.data;
    
  } catch (error) {
    console.error("❌ Failed to fetch pricing config:", error.message);
    
    // Log more details if available
    if (error.response) {
      console.error("Status:", error.response.status);
      console.error("Headers:", error.response.headers);
      if (isHTMLResponse(error.response.data)) {
        console.error("Received HTML instead of JSON - check Django URL configuration");
      }
    }
    
    // Return default pricing so the page doesn't break
    return {
      academic_levels: {
        "High School": 10,
        "College": 12,
        "Undergraduate": 15,
        "Bachelor": 18,
        "Master": 22,
        "PhD": 28,
        "Doctoral": 35
      },
      deadlines: [
        { range: '0-24', percentage: 80, display_name: 'Urgent (24 hours or less)' },
        { range: '25-72', percentage: 60, display_name: 'Fast (1-3 days)' },
        { range: '73-168', percentage: 30, display_name: 'Standard (3-7 days)' },
        { range: '169-336', percentage: 10, display_name: 'Relaxed (7-14 days)' },
        { range: '337+', percentage: 0, display_name: 'Extended (14+ days)' }
      ],
      additional_services: [
        { type: 'PLAGIARISM', fee_type: 'FIXED', price: 10, name: 'Plagiarism Report', description: 'Turnitin report included' },
        { type: 'EXECUTIVE_SUMMARY', fee_type: 'FIXED', price: 15, name: 'Executive Summary', description: '1-page summary of key points' },
        { type: 'EXPERT_WRITER', fee_type: 'FIXED', price: 25, name: 'Expert Writer', description: 'Top-rated specialist in your field' }
      ],
      site_settings: { minimum_order: 0, enable_rush_fees: true }
    };
  }
};

// Save academic levels
export const saveAcademicLevels = async (academicLevels) => {
  try {
    console.log("💰 Saving academic levels:", academicLevels);
    const response = await api.post("/api/administration/pricing/academic-levels/", {
      academic_levels: academicLevels
    });
    console.log("✅ Academic levels saved:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to save academic levels:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Save deadlines
export const saveDeadlines = async (deadlines) => {
  try {
    console.log("💰 Saving deadlines:", deadlines);
    const response = await api.post("/api/administration/pricing/deadlines/", {
      deadlines: deadlines
    });
    console.log("✅ Deadlines saved:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to save deadlines:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Save additional services
export const saveAdditionalServices = async (services) => {
  try {
    console.log("💰 Saving services:", services);
    const response = await api.post("/api/administration/pricing/services/", {
      services: services
    });
    console.log("✅ Services saved:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to save services:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Save page discounts
export const savePageDiscounts = async (discounts) => {
  try {
    console.log("💰 Saving page discounts:", discounts);
    const response = await api.post("/api/administration/pricing/page-discounts/", {
      discounts: discounts
    });
    console.log("✅ Page discounts saved:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to save page discounts:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Save subject multipliers
export const saveSubjectMultipliers = async (subjects) => {
  try {
    console.log("💰 Saving subject multipliers:", subjects);
    const response = await api.post("/api/administration/pricing/subject-multipliers/", {
      subjects: subjects
    });
    console.log("✅ Subject multipliers saved:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to save subject multipliers:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Save site settings
export const saveSiteSettings = async (settings) => {
  try {
    console.log("💰 Saving site settings:", settings);
    const response = await api.post("/api/administration/pricing/site-settings/", settings);
    console.log("✅ Site settings saved:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to save site settings:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Save all pricing data at once
export const saveAllPricing = async (pricingData) => {
  try {
    console.log("💰 Saving all pricing data:", pricingData);
    const response = await api.post("/api/administration/pricing/save-all/", pricingData);
    console.log("✅ All pricing saved:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Failed to save all pricing:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Forgot Password API calls
export const forgotPassword = async (email) => {
  try {
    const response = await fetch('/api/accounts/password-reset/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send reset email');
    }

    return data;
  } catch (error) {
    console.error('Forgot password error:', error);
    throw error;
  }
};

export const resetPassword = async ({ uid, token, new_password, confirm_password }) => {
  try {
    const response = await fetch('/api/accounts/password-reset/confirm/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        uid, 
        token, 
        new_password, 
        confirm_password 
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to reset password');
    }

    return data;
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
};

export const validateResetToken = async (uid, token) => {
  try {
    const response = await fetch('/api/accounts/password-reset/validate-token/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uid, token }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Invalid token');
    }

    return data;
  } catch (error) {
    console.error('Validate token error:', error);
    throw error;
  }
};

// ===== ADMIN SAMPLES FUNCTIONS =====
// FIXED: Using correct URLs based on Django router

// Get all samples for admin (including drafts, under review, etc.)
export const getAdminSamples = async (params = {}) => {
  try {
    console.log("📄 Admin: Fetching all samples with params:", params);
    
    // CORRECT URL: /samples/samples/ (the router pattern)
    const url = "/samples/samples/";
    console.log(`🌐 Full URL being called: ${window.location.origin}${url}`);
    
    const response = await api.get(url, { 
      params: {
        ...params,
        admin_view: true,
        page_size: params.page_size || 1000
      } 
    });
    
    console.log(`✅ Admin: Found ${response.data.results?.length || response.data.length} samples`);
    return response.data;
  } catch (error) {
    console.error("❌ Admin: Failed to fetch samples:", error);
    throw error;
  }
};

// Get single sample for admin editing
export const getAdminSample = async (sampleId) => {
  try {
    console.log(`📄 Admin: Fetching sample ${sampleId}...`);
    const response = await api.get(`/samples/samples/${sampleId}/`);
    console.log("✅ Admin: Sample fetched successfully");
    return response.data;
  } catch (error) {
    console.error(`❌ Admin: Failed to fetch sample ${sampleId}:`, error);
    throw error;
  }
};

// Create new sample (admin only)
export const createAdminSample = async (formData) => {
  try {
    console.log("📤 Admin: Creating new sample...");
    
    const response = await api.post("/samples/samples/", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`📤 Upload progress: ${percent}%`);
        }
      }
    });
    
    console.log("✅ Admin: Sample created successfully:", response.data.id);
    return response.data;
  } catch (error) {
    console.error("❌ Admin: Failed to create sample:", error);
    throw error;
  }
};

// Update sample (admin only)
export const updateAdminSample = async (sampleId, formData) => {
  try {
    console.log(`📤 Admin: Updating sample ${sampleId}...`);
    
    const response = await api.put(`/samples/samples/${sampleId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`📤 Upload progress: ${percent}%`);
        }
      }
    });
    
    console.log("✅ Admin: Sample updated successfully");
    return response.data;
  } catch (error) {
    console.error(`❌ Admin: Failed to update sample ${sampleId}:`, error);
    throw error;
  }
};

// Delete sample (admin only)
export const deleteAdminSample = async (sampleId) => {
  try {
    console.log(`🗑️ Admin: Deleting sample ${sampleId}...`);
    
    const response = await api.delete(`/samples/samples/${sampleId}/`);
    
    console.log("✅ Admin: Sample deleted successfully");
    return response.data;
  } catch (error) {
    console.error(`❌ Admin: Failed to delete sample ${sampleId}:`, error);
    throw error;
  }
};

// Get admin samples statistics
export const getAdminSamplesStats = async () => {
  try {
    console.log("📊 Admin: Fetching samples statistics...");
    
    const response = await api.get("/samples/samples/statistics/");
    
    console.log("✅ Admin: Statistics fetched successfully");
    return response.data;
  } catch (error) {
    console.error("❌ Admin: Failed to fetch statistics:", error);
    
    // Return default stats if endpoint doesn't exist
    return {
      total_samples: 0,
      total_views: 0,
      total_downloads: 0,
      by_paper_type: [],
      by_academic_level: []
    };
  }
};

// Get admin categories
export const getAdminCategories = async () => {
  try {
    console.log("📁 Admin: Fetching categories...");
    const response = await api.get("/samples/categories/");
    console.log(`✅ Admin: Found ${response.data.length} categories`);
    return response.data;
  } catch (error) {
    console.error("❌ Admin: Failed to fetch categories:", error);
    return [];
  }
};

// Get admin tags
export const getAdminTags = async () => {
  try {
    console.log("🏷️ Admin: Fetching tags...");
    const response = await api.get("/samples/tags/");
    console.log(`✅ Admin: Found ${response.data.length} tags`);
    return response.data;
  } catch (error) {
    console.error("❌ Admin: Failed to fetch tags:", error);
    return [];
  }
};

export default api;
